import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authService } from '@/services/auth'
import toast from 'react-hot-toast'
import analytics from '@/utils/analytics'

interface User {
  id: number
  username: string
  email: string
  full_name?: string
}

interface AuthState {
  token: string | null
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitializing: boolean
  login: (username: string, password: string) => Promise<void>
  loginWithGoogle: () => Promise<void>
  signup: (email: string, username: string, password: string, full_name?: string) => Promise<any>
  sendOtp: (email: string, username?: string) => Promise<void>
  verifyOtp: (email: string, otp: string) => Promise<void>
  signupWithVerification: (username: string, email: string, password: string, full_name?: string) => Promise<void>
  logout: () => void
  checkAuth: () => Promise<void>
  clearAuth: () => void
  handleOAuthCallback: (token: string, user: any) => void
  hasOAuthToken: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isInitializing: false,

      login: async (username: string, password: string) => {
        set({ isLoading: true })
        try {
          const response = await authService.login(username, password)
          // Ensure token is stored in localStorage immediately
          localStorage.setItem('jpdz_token', response.token)
          set({
            token: response.token,
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          })
          toast.success(`Welcome back, ${response.user.username}!`)
          analytics.login('email')
        } catch (error: any) {
          set({ isLoading: false })
          const errorMessage = error.response?.data?.message ||
            error.response?.data?.error ||
            'Failed to login'
          toast.error(errorMessage)
          throw error
        }
      },

      signup: async (email: string, username: string, password: string, full_name?: string) => {
        set({ isLoading: true })
        try {
          const response = await authService.signup(email, username, password, full_name)

          // Check if response includes token (immediate login) or requires verification
          if (response.token && response.user) {
            // Immediate login - backend returned token
            set({
              token: response.token,
              user: response.user!,
              isAuthenticated: true,
              isLoading: false,
            })
            localStorage.setItem('jpdz_token', response.token)
            toast.success(`Welcome to JPDZ, ${response.user?.username || 'User'}!`)
            analytics.signup('email')
          } else if (response.requires_verification) {
            // Requires email verification - return response for redirect
            set({ isLoading: false })
            toast.success('Account created! Please check your email for verification code.')
            return response // Return response so component can handle redirect
          } else {
            // Unexpected response format
            set({ isLoading: false })
            toast.success('Account created successfully!')
            return response
          }
        } catch (error: any) {
          set({ isLoading: false })
          const errorMessage = error.response?.data?.message ||
            error.response?.data?.error ||
            'Failed to create account'
          toast.error(errorMessage)
          throw error
        }
      },

      sendOtp: async (email: string, username?: string) => {
        set({ isLoading: true })
        try {
          await authService.sendOtp(email, username)
          toast.success('Verification code sent to your email!')
        } catch (error: any) {
          const errorMessage = error.response?.data?.message ||
            error.response?.data?.error ||
            'Failed to send verification code'
          toast.error(errorMessage)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      verifyOtp: async (email: string, otp: string) => {
        set({ isLoading: true })
        try {
          await authService.verifyOtp(email, otp)
          // Don't set authentication state here - that happens in signupWithVerification
        } catch (error: any) {
          set({ isLoading: false })
          const errorMessage = error.response?.data?.message ||
            error.response?.data?.error ||
            'Invalid verification code'
          toast.error(errorMessage)
          throw error
        }
      },

      signupWithVerification: async (username: string, email: string, password: string, full_name?: string) => {
        try {
          const response = await authService.registerWithVerification(username, email, password, full_name)
          set({
            token: response.token,
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          })
          toast.success(`Welcome to JPDZ, ${response.user?.username || 'User'}!`)
        } catch (error: any) {
          set({ isLoading: false })
          const errorMessage = error.response?.data?.message ||
            error.response?.data?.error ||
            'Failed to complete registration'
          toast.error(errorMessage)
          throw error
        }
      },

      loginWithGoogle: async () => {
        set({ isLoading: true })
        try {
          const response = await fetch('/api/auth/google/login')
          const data = await response.json()

          if (!data.success) {
            throw new Error(data.error || 'Failed to initiate Google login')
          }

          // Redirect to Google OAuth
          window.location.href = data.auth_url

        } catch (error: any) {
          set({ isLoading: false })
          const errorMessage = error.message || 'Failed to start Google login'
          toast.error(errorMessage)
          throw error
        }
      },

      logout: () => {
        const state = get()
        if (state.token) {
          authService.logout()
        }
        // Clear both zustand state and localStorage
        localStorage.removeItem('jpdz_token')
        set({
          token: null,
          user: null,
          isAuthenticated: false,
        })
        analytics.logout()
        toast.success('Logged out successfully')
      },

      checkAuth: async () => {
        const state = get()


        // Set initializing state to prevent premature redirects
        set({ isInitializing: true })

        // Check for OAuth callback token in URL
        const urlParams = new URLSearchParams(window.location.search)
        const oauthToken = urlParams.get('token')
        const welcomeFlag = urlParams.get('welcome')


        if (oauthToken) {
          // Handle OAuth callback - store token first, then verify
          try {
            // Store the token from URL immediately
            localStorage.setItem('jpdz_token', oauthToken)

            // Now verify the token
            const result = await authService.verify(oauthToken)

            if (result.success && result.data?.valid) {
              const user = result.data.user
              set({
                token: oauthToken,
                isAuthenticated: true,
                isInitializing: false,
                user: {
                  id: user.id,
                  username: user.username,
                  email: user.email,
                  full_name: user.full_name
                }
              })

              // Show welcome message if new user and set first-time user flag for onboarding
              if (welcomeFlag === 'true') {
                localStorage.setItem('jpdz_first_time_user', 'true')
                toast.success('Welcome to JPDZ! Your account has been created successfully.')
              } else {
                toast.success('Successfully signed in with Google!')
              }


              // Clean up URL after a delay to ensure routing is complete
              setTimeout(() => {
                const url = new URL(window.location.href)
                url.searchParams.delete('token')
                url.searchParams.delete('welcome')
                window.history.replaceState({}, '', url.toString())
              }, 500)

              return
            } else {
              // Token invalid, clean up
              localStorage.removeItem('jpdz_token')
              set({ isInitializing: false })
            }
          } catch (error) {
            localStorage.removeItem('jpdz_token')

            // Clean up URL
            const url = new URL(window.location.href)
            url.searchParams.delete('token')
            url.searchParams.delete('welcome')
            window.history.replaceState({}, '', url.toString())

            // Provide specific error messages based on error type
            let errorMessage = 'Authentication failed. Please try again.'
            if (error instanceof Error) {
              if (error.message.includes('network')) {
                errorMessage = 'Network error during authentication. Please check your connection and try again.'
              } else if (error.message.includes('timeout')) {
                errorMessage = 'Authentication timed out. Please try again.'
              } else if (error.message.includes('401')) {
                errorMessage = 'Invalid authentication token. Please sign in again.'
              }
            }

            toast.error(errorMessage)
            set({ isInitializing: false })
            // Don't use window.location.href here - it causes reload loops
            // Token is already cleared, React will naturally redirect to login
          }
        }

        // Normal token verification for existing tokens or secure cookie
        const tokenToVerify = state.token || localStorage.getItem('jpdz_token')
        if (tokenToVerify) {
          try {
            // Verify token with backend
            const result = await authService.verify(tokenToVerify)

            if (result.success && result.data?.valid) {
              const user = result.data.user
              set({
                token: tokenToVerify,
                isAuthenticated: true,
                isInitializing: false,
                user: {
                  id: user.id,
                  username: user.username,
                  email: user.email,
                  full_name: user.full_name
                }
              })
            } else {
              // Token is invalid, clear auth state and localStorage
              localStorage.removeItem('jpdz_token')
              set({
                token: null,
                user: null,
                isAuthenticated: false,
                isInitializing: false,
              })
            }
          } catch (error) {
            // Clear invalid token from localStorage
            localStorage.removeItem('jpdz_token')
            set({
              token: null,
              user: null,
              isAuthenticated: false,
              isInitializing: false,
            })
          }
        } else {
          // No token in localStorage/Zustand. We might still be authenticated via HttpOnly cookie.
          try {
            const result = await authService.verify()
            if (result && result.success && result.data?.valid) {
              const user = result.data.user
              set({
                token: localStorage.getItem('jpdz_token') || '',
                isAuthenticated: true,
                isInitializing: false,
                user: {
                  id: user.id,
                  username: user.username,
                  email: user.email,
                  full_name: user.full_name
                }
              })
              return
            }
          } catch (e) {
          }
          // Finish initialization unauthenticated
          set({ isInitializing: false })
        }
      },

      handleOAuthCallback: (token: string, user: any) => {
        localStorage.setItem('jpdz_token', token)
        set({
          token,
          user,
          isAuthenticated: true,
          isLoading: false,
        })
        toast.success(`Welcome ${user.username}!`)
      },

      clearAuth: () => {
        localStorage.removeItem('jpdz_token')
        set({
          token: null,
          user: null,
          isAuthenticated: false,
        })
        toast.success('Authentication cleared')
      },

      hasOAuthToken: () => {
        const urlParams = new URLSearchParams(window.location.search)
        return urlParams.has('token')
      },
    }),
    {
      name: 'jpdz-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated
      }),
      // Ensure token is synced with localStorage
      onRehydrateStorage: () => (state) => {
        if (state) {
          const localToken = localStorage.getItem('jpdz_token')

          // Sync token from localStorage if it exists but zustand doesn't have it
          if (localToken && !state.token) {
            state.token = localToken
          }
          // Or if zustand has token but localStorage doesn't
          else if (state.token && !localToken) {
            localStorage.setItem('jpdz_token', state.token)
          }
          // Clear inconsistent state
          else if (!localToken && !state.token) {
            state.isAuthenticated = false
            state.user = null
          }
        }
      },
    }
  )
)
